/* 707dot collaborators carousel
   Six linked posters with bounded or seamless infinite movement. */

(() => {
  const ELEMENT_NAME = 'seven-collaborator-carousel';
  if (customElements.get(ELEMENT_NAME)) return;

  class SevenCollaboratorCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.progress = this.querySelector('[data-carousel-progress]');
      this.status = this.querySelector('[data-carousel-status]');
      this.originalSlides = Array.from(this.querySelectorAll('[data-carousel-original]'));

      if (!this.viewport || !this.track || this.originalSlides.length === 0) return;

      this.mode = this.dataset.loopMode === 'bounded' ? 'bounded' : 'infinite';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.designMode = Boolean(window.Shopify && Shopify.designMode);
      this.autoplay = this.mode === 'infinite' && this.dataset.autoplay === 'true' && !this.reduceMotion && !this.designMode;
      this.speed = Math.max(2, Number.parseFloat(this.dataset.speed || '8'));
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
      this.hovered = false;
      this.focused = false;
      this.userInteracting = false;
      this.dragging = false;
      this.suppressClickUntil = 0;
      this.updateFrame = 0;
      this.animationFrame = 0;
      this.lastAnimationTime = 0;
      this.boundAnimate = this.animate.bind(this);

      if (this.mode === 'infinite' && this.originalSlides.length > 1) {
        this.createInfiniteCopies();
      }

      this.bindEvents();

      requestAnimationFrame(() => {
        this.refreshGeometry(true);
        this.queueUpdate();
        if (this.autoplay) this.animationFrame = requestAnimationFrame(this.boundAnimate);
      });
    }

    disconnectedCallback() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.boundRefreshGeometry);
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      window.clearTimeout(this.interactionTimer);
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-carousel-original');
      clone.setAttribute('data-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    createInfiniteCopies() {
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      this.beforeSlides = this.originalSlides.map((slide) => this.createClone(slide));
      this.afterSlides = this.originalSlides.map((slide) => this.createClone(slide));
      this.beforeSlides.forEach((slide) => beforeFragment.appendChild(slide));
      this.afterSlides.forEach((slide) => afterFragment.appendChild(slide));
      this.track.insertBefore(beforeFragment, this.track.firstChild);
      this.track.appendChild(afterFragment);
    }

    bindEvents() {
      this.viewport.addEventListener('scroll', () => this.queueUpdate(), { passive: true });

      this.previousButton?.addEventListener('click', () => this.moveBySlide(-1, true));
      this.nextButton?.addEventListener('click', () => this.moveBySlide(1, true));

      this.viewport.addEventListener('keydown', (event) => {
        if (event.target !== this.viewport) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.moveBySlide(event.key === 'ArrowRight' ? 1 : -1, true);
      });

      this.addEventListener('mouseenter', () => {
        this.hovered = true;
      });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
      });
      this.addEventListener('focusin', () => {
        this.focused = true;
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.focused = false;
      });

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.viewport.addEventListener('pointerup', (event) => this.onPointerEnd(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerEnd(event));
      this.viewport.addEventListener('lostpointercapture', () => this.finishPointerInteraction());

      this.viewport.addEventListener(
        'click',
        (event) => {
          if (Date.now() < this.suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );

      this.viewport.addEventListener(
        'wheel',
        () => {
          this.pauseForInteraction(900);
        },
        { passive: true }
      );

      this.boundRefreshGeometry = () => this.refreshGeometry(false);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.boundRefreshGeometry);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.boundRefreshGeometry, { passive: true });
      }

      this.boundVisibilityChange = () => {
        this.lastAnimationTime = 0;
      };
      document.addEventListener('visibilitychange', this.boundVisibilityChange);
    }

    onPointerDown(event) {
      this.userInteracting = true;

      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.pointerActive = true;
      this.dragging = false;
      this.pointerStartX = event.clientX;
      this.pointerStartScroll = this.viewport.scrollLeft;
    }

    onPointerMove(event) {
      if (!this.pointerActive) return;

      const distance = event.clientX - this.pointerStartX;
      if (Math.abs(distance) > 6) {
        this.dragging = true;
        this.viewport.classList.add('is-dragging');
        if (!this.viewport.hasPointerCapture(event.pointerId)) {
          this.viewport.setPointerCapture(event.pointerId);
        }
      }

      if (!this.dragging) return;
      event.preventDefault();
      this.viewport.scrollLeft = this.pointerStartScroll - distance;
    }

    onPointerEnd(event) {
      if (this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }
      this.finishPointerInteraction();
    }

    finishPointerInteraction() {
      if (this.dragging) this.suppressClickUntil = Date.now() + 220;
      this.pointerActive = false;
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
      this.pauseForInteraction(650);
    }

    pauseForInteraction(duration) {
      this.userInteracting = true;
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = window.setTimeout(() => {
        this.userInteracting = false;
        this.lastAnimationTime = 0;
      }, duration);
    }

    refreshGeometry(resetPosition) {
      const oldStart = this.setStart || 0;
      const oldWidth = this.setWidth || 0;
      const oldProgress = oldWidth > 0 ? (this.viewport.scrollLeft - oldStart) / oldWidth : 0;

      if (this.originalSlides.length > 1) {
        this.slideStep = this.originalSlides[1].offsetLeft - this.originalSlides[0].offsetLeft;
      } else {
        const style = window.getComputedStyle(this.track);
        this.slideStep = this.originalSlides[0].offsetWidth + Number.parseFloat(style.columnGap || style.gap || '0');
      }

      if (this.mode === 'infinite' && this.afterSlides?.length) {
        this.setStart = this.originalSlides[0].offsetLeft;
        this.setEnd = this.afterSlides[0].offsetLeft;
        this.setWidth = this.setEnd - this.setStart;

        if (resetPosition || !this.positioned) {
          this.viewport.scrollLeft = this.setStart;
          this.positioned = true;
        } else if (this.setWidth > 0) {
          const wrappedProgress = ((oldProgress % 1) + 1) % 1;
          this.viewport.scrollLeft = this.setStart + wrappedProgress * this.setWidth;
        }
      }

      this.queueUpdate();
    }

    normalizeInfinitePosition() {
      if (this.mode !== 'infinite' || !this.setWidth || this.normalizing) return;

      let nextPosition = this.viewport.scrollLeft;
      if (nextPosition >= this.setEnd) nextPosition -= this.setWidth;
      if (nextPosition < this.setStart) nextPosition += this.setWidth;

      if (Math.abs(nextPosition - this.viewport.scrollLeft) > 0.5) {
        this.normalizing = true;
        this.viewport.scrollLeft = nextPosition;
        this.normalizing = false;
      }
    }

    moveBySlide(direction, announce) {
      if (!this.slideStep) return;

      this.pauseForInteraction(1100);
      this.viewport.scrollBy({
        left: this.slideStep * direction,
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });

      if (announce) window.setTimeout(() => this.announceCurrent(), this.reduceMotion ? 0 : 420);
    }

    queueUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = 0;
        this.normalizeInfinitePosition();
        this.updateActiveSlide();
        this.updateControls();
      });
    }

    updateActiveSlide() {
      const slides = Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
      if (!slides.length) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let nearestSlide = slides[0];
      let nearestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
        slide.classList.remove('is-active');
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestSlide = slide;
        }
      });

      nearestSlide.classList.add('is-active');
      const activeIndex = nearestSlide.dataset.slideIndex || '0';
      this.activeIndex = Number.parseInt(activeIndex, 10) || 0;

      this.originalSlides.forEach((slide) => {
        if (slide.dataset.slideIndex === activeIndex) {
          slide.setAttribute('aria-current', 'true');
        } else {
          slide.removeAttribute('aria-current');
        }
      });
    }

    updateControls() {
      if (this.mode === 'bounded') {
        const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        if (this.previousButton) this.previousButton.disabled = this.viewport.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2;
        if (this.progress) {
          const progress = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 1;
          this.progress.style.transform = `scaleX(${Math.max(0, Math.min(1, progress))})`;
        }
      } else if (this.progress) {
        const progress = (this.activeIndex + 1) / this.originalSlides.length;
        this.progress.style.transform = `scaleX(${Math.max(0, Math.min(1, progress))})`;
      }
    }

    announceCurrent() {
      if (!this.status) return;
      this.status.textContent = `Poster ${this.activeIndex + 1} of ${this.originalSlides.length}`;
    }

    shouldPauseAutoplay() {
      return (
        document.hidden ||
        this.userInteracting ||
        this.focused ||
        (this.pauseOnHover && this.hovered)
      );
    }

    animate(time) {
      if (!this.isConnected || !this.autoplay) return;

      if (!this.lastAnimationTime) this.lastAnimationTime = time;
      const elapsed = Math.min(64, time - this.lastAnimationTime);
      this.lastAnimationTime = time;

      if (!this.shouldPauseAutoplay()) {
        this.viewport.scrollLeft += (this.speed * elapsed) / 1000;
        this.normalizeInfinitePosition();
        this.queueUpdate();
      }

      this.animationFrame = requestAnimationFrame(this.boundAnimate);
    }
  }

  customElements.define(ELEMENT_NAME, SevenCollaboratorCarousel);
})();
